import { RichTextInput } from '@/components/RichTextInput/RichTextInput';
import { CommunicationThreadTopic } from '@bariendo/lib/constants';
import {
  ActionIcon,
  Box,
  Card,
  FileInput,
  Flex,
  Group,
  Loader,
  Menu,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import { formatHumanName, getReferenceString } from '@medplum/core';
import { IconChevronDown, IconMessage, IconPaperclip, IconSend, IconX } from '@tabler/icons-react';
import commonStyles from '@theme/common.module.css';
import { useMemo } from 'react';
import { usePostHog } from 'posthog-js/react';
import { trackMessageDraftAbandoned } from '@/services/analytics';
import { FileInputListDisplay } from './FileInputListDisplay';
import { MessageBoxPDFPreview } from './MessageBoxPDFPreview';
import { EnabledTopic } from '@/services/timeline-threads';
import { useTimelineContext } from '@/hooks/useTimelineContext';
import { useSendChatMessage } from '@/pages/PatientTimelinePage/hooks/useSendChatMessage';
import { getPatientName } from '@/pages/PatientTimelinePage/services/timelinePatient';

export function ChatMessageBox({ enabledTopics }: { enabledTopics: EnabledTopic[] }) {
  const {
    textareaInputRef,
    isSending,
    message,
    setMessage,
    richTextContent,
    setRichTextContent,
    handleSendMessage,
    fileInputRef,
    files,
    handleSelectFiles,
    removeFile,
    handleAttachClick,
    pdfModalOpened,
    handlePdfPreview,
    handleClosePdfModal,
    selectedPdfFile,
    pdfUrl,
    formatFileSize,
  } = useSendChatMessage();

  const { patientContext, careTeamContext, messageBoxContext } = useTimelineContext();

  const patient = patientContext?.patient;
  const { careTeamProviders } = careTeamContext ?? {};

  const { topic, setTopic, getTopicLabel, setShowMessageInput } = messageBoxContext;
  const posthog = usePostHog();

  const patientName = useMemo(() => {
    return patient ? getPatientName(patient) : 'Unknown Patient';
  }, [patient]);

  const hasMultipleTopics = enabledTopics.length > 1;

  return (
    <>
      <Card withBorder padding={0} radius="lg" style={{ backgroundColor: '#ffffff' }}>
        <Box style={{ borderBottom: '1px solid #e9ecef' }}>
          <Group w="100%" p="xs" justify="space-between" align="center">
            <Group>
              <ThemeIcon variant="filled" radius="lg" size="lg" bg="#375462">
                <IconMessage size={16} />
              </ThemeIcon>
              <Flex gap="xs" align="center" justify="space-between">
                <Stack gap={2}>
                  <Flex gap="xs" align="space-between">
                    <Text variant="header">Chat with {patientName}</Text>
                  </Flex>
                  <Group gap="0" align="center">
                    <Text variant="caption" tt="capitalize">
                      {getTopicLabel(topic)} thread
                    </Text>
                    {hasMultipleTopics && (
                      <Menu shadow="md" width={200} position="bottom-start">
                        <Menu.Target>
                          <ActionIcon
                            size="md"
                            variant="subtle"
                            color="gray"
                            style={{ minWidth: 'auto', padding: '2px' }}
                          >
                            <IconChevronDown size={12} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown style={{ borderRadius: '8px' }}>
                          <Menu.Label>Choose a topic</Menu.Label>
                          {enabledTopics.map((enbabledTopic) => (
                            <Menu.Item
                              key={enbabledTopic.code}
                              onClick={() => setTopic(enbabledTopic.code as CommunicationThreadTopic)}
                              style={{
                                backgroundColor: enbabledTopic.code === topic ? '#f8f9fa' : 'transparent',
                              }}
                            >
                              {enbabledTopic.display}
                            </Menu.Item>
                          ))}
                        </Menu.Dropdown>
                      </Menu>
                    )}
                  </Group>
                </Stack>
              </Flex>
            </Group>
            <Tooltip label="Close" withArrow position="top-end" openDelay={500}>
              <ActionIcon
                variant="subtle"
                size="md"
                radius="xl"
                aria-label="Close"
                onClick={() => {
                  // Track draft abandonment if there's unsent content
                  if (message.trim() && patient?.id) {
                    trackMessageDraftAbandoned(posthog, {
                      message_type: 'chat',
                      draft_length: message.length,
                      had_attachments: files.length > 0,
                      patient_id: patient.id,
                    });
                  }
                  setShowMessageInput(false);
                }}
                className={commonStyles.hover}
              >
                <IconX size={16} color="#6c757d" />
              </ActionIcon>
            </Tooltip>
          </Group>
          {/* Content */}
          <Box p="lg" style={{ borderTop: '1px solid #e9ecef' }}>
            <Flex gap="xs" align="center">
              <RichTextInput
                value={richTextContent}
                onChange={(text, json) => {
                  setMessage(text);
                  setRichTextContent(json);
                }}
                textareaInputRef={textareaInputRef as React.RefObject<HTMLDivElement>}
                disabled={isSending}
                placeholder="Write a message..."
                mentionItems={(careTeamProviders ?? []).map((provider) => ({
                  id: getReferenceString(provider) ?? '',
                  label: formatHumanName(provider.name?.[0]),
                }))}
                editorKey={`${patient?.id}-${(careTeamProviders ?? []).length}`}
              />
              {/* Hidden FileInput that can be triggered programmatically */}
              <FileInput
                accept="application/pdf,image/*"
                multiple
                style={{ display: 'none' }}
                ref={fileInputRef}
                value={files}
                onChange={handleSelectFiles}
                disabled={isSending}
              />
              <Tooltip label="Attach a file" withArrow position="top-end" openDelay={500}>
                <ActionIcon
                  variant="subtle"
                  size="lg"
                  radius="xl"
                  aria-label="Attach"
                  onClick={handleAttachClick}
                  disabled={isSending}
                  className={commonStyles.hover}
                >
                  <IconPaperclip size={20} color="#212529" />
                </ActionIcon>
              </Tooltip>
              <Tooltip
                label={message.length > 0 || files.length > 0 ? 'Send message' : 'Add a message or attachment'}
                withArrow
                position="top-end"
                openDelay={400}
                color="#258195"
              >
                <ActionIcon
                  variant="filled"
                  size="lg"
                  radius="xl"
                  color="#246A7A"
                  aria-label="Send"
                  onClick={() => handleSendMessage(topic)}
                  disabled={message.length === 0 && files.length === 0}
                >
                  {isSending ? <Loader size={16} color="white" /> : <IconSend size={20} />}
                </ActionIcon>
              </Tooltip>
            </Flex>
            {/* Attachment display */}
            <FileInputListDisplay
              files={files}
              formatFileSize={formatFileSize}
              handlePdfPreview={handlePdfPreview}
              removeFile={removeFile}
            />
          </Box>
        </Box>
      </Card>
      {/* PDF Preview Modal */}
      <MessageBoxPDFPreview
        opened={pdfModalOpened}
        onClose={handleClosePdfModal}
        file={selectedPdfFile}
        pdfUrl={pdfUrl}
      />
    </>
  );
}
